// Persisted discovery filters — powers the "Save Filters" button on the
// events discover page. localStorage only; pure functions, no React.

const STORAGE_KEY = "pathcollective:discoverFilters"

export type SavedDiscoverFilters = {
  date: string
  subcategories: string[]
  prices: string[]
  format: string[]
  location: string[]
  suburb: string
}

const stringArray = (value: unknown, fallback: string[]): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : fallback

// Absolute dates ("2026-07-10") and ranges ("2026-07-10:2026-07-12") go stale,
// unlike the relative quick options ("Today", "This Weekend"). Drop them once
// fully in the past so a restored filter set can't boot the page permanently
// empty (same time-bomb class as the refund-reports CSV incident, PR #296).
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function pruneStaleDate(date: string): string {
  let end: string | null = null
  if (ISO_DATE.test(date)) {
    end = date
  } else if (date.includes(":")) {
    const [from, to] = date.split(":")
    if (to && ISO_DATE.test(to)) end = to
    else if (from && ISO_DATE.test(from)) end = from
    else return ""
  } else {
    return date // relative quick option — ages gracefully
  }
  return new Date(`${end}T23:59:59`).getTime() < Date.now() ? "" : date
}

export function saveDiscoverFilters(filters: SavedDiscoverFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  } catch {
    // Storage unavailable/full — saving filters is best-effort
  }
}

export function loadDiscoverFilters(): SavedDiscoverFilters | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<SavedDiscoverFilters> | null
    if (typeof parsed !== "object" || parsed === null) return null
    return {
      date: typeof parsed.date === "string" ? pruneStaleDate(parsed.date) : "",
      subcategories: stringArray(parsed.subcategories, []),
      prices: stringArray(parsed.prices, ["Free", "Paid", "Donation"]),
      format: stringArray(parsed.format, ["In-person", "Online"]),
      location: stringArray(parsed.location, []),
      suburb: typeof parsed.suburb === "string" ? parsed.suburb : "",
    }
  } catch {
    return null
  }
}

export function clearSavedDiscoverFilters(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore — nothing to clear or storage unavailable
  }
}

// #1179 FR-3 — why Discover is showing nothing. "Try adjusting the filters" is
// only true when a filter is actually narrowing a non-empty catalogue.
// prices/format start with every option ticked, which is "no restriction".
export function hasActiveDiscoverFilters(filters: Record<string, unknown> | null | undefined): boolean {
  if (!filters) return false
  const text = (v: unknown) => typeof v === "string" && v.trim().length > 0
  const some = (v: unknown) => Array.isArray(v) && v.length > 0
  const partial = (v: unknown, all: number) => Array.isArray(v) && v.length > 0 && v.length < all
  return (
    text(filters.date) ||
    text(filters.facilitator) ||
    text(filters.smartPrompt) ||
    text(filters.suburb) ||
    some(filters.subcategories) ||
    some(filters.location) ||
    partial(filters.prices, 3) ||
    partial(filters.format, 2)
  )
}

export type DiscoverEmptyState = "saved" | "filtered" | "nothing-listed"

export function discoverEmptyState(input: {
  totalEvents: number
  filters: Record<string, unknown> | null | undefined
  showOnlySaved: boolean
}): DiscoverEmptyState {
  if (input.showOnlySaved) return "saved"
  if (input.totalEvents > 0 && hasActiveDiscoverFilters(input.filters)) return "filtered"
  return "nothing-listed"
}
